import React, { useEffect, useMemo, useRef } from "react";
import { Form, Row, Col, Select, InputNumber, message, Switch } from "antd";
import { useTranslation } from "react-i18next";
import CustomModal from "components/Common/CustomModal";
import CommonSelectLite from "components/Common/CommonSelectLite";

export type PayrollLineFormValues = {
  lineType: "salary" | "bonus" | "deduction";
  sourceType: "salary" | "Leave" | "other" | "CNSS";
  amount: number;
  status?: "pending" | "paid" | "partially_paid" | "failed" | "overdue" | "coming";
};

type Props = {
  visible: boolean;                         // like your TaskModal
  onClose: () => void;                      // like your TaskModal
  onSubmit: (values: PayrollLineFormValues) => Promise<boolean> | void; // returns validated values
  initialValues?: Partial<PayrollLineFormValues>;
};

const PayrollLineModal: React.FC<Props> = ({
  visible,
  onClose,
  onSubmit,
  initialValues,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<PayrollLineFormValues>();
  const isEditing = !!initialValues;
  const netTouchedRef = useRef(false);

  const lineTypeOptions = useMemo(() => {
    return [
      // { value: "base", label: t("payroll.lineType.base") },
      { value: "bonus", label: t("payroll.lineType.bonus") },
      { value: "deduction", label: t("payroll.lineType.deduction") },
    ];
  }, [t]);

  const sourceTypeOptions = useMemo(() => {
    return [
      { value: "contract", label: t("payroll.sourceType.contract") },
      { value: "leave", label: t("payroll.sourceType.leave") },
      { value: "cnss", label: t("payroll.sourceType.cnss") },
      { value: "other", label: t("payroll.sourceType.other") },
    ];
  }, [t]);

  const mergedInitial = useMemo<Partial<PayrollLineFormValues>>(
    () => ({
      status: "pending",
      ...initialValues,
    }),
    [initialValues]
  );

  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue(mergedInitial);
      netTouchedRef.current = false;
    }
  }, [visible, mergedInitial, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload: PayrollLineFormValues = {
        ...values,
        amount: values?.amount ?? 0,
        // grossAmount: values?.netAmount ?? 0,
        // netAmount:
        //   values.netAmount === undefined || values.netAmount === null
        //     ? values.grossAmount
        //     : values.netAmount,
      };
      const response = await onSubmit(payload);
      if (response) {
        form.resetFields();
      }
    } catch (error) {
      message.error(t("errors.VALIDATION_ERROR"));
    }
  };

  return (
    <CustomModal
      title={isEditing ? t("payroll.edit_line") : t("payroll.add_line")}
      isOpen={visible}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      onSave={handleSave}
      saveText={isEditing ? t("update") : t("create")}
    >
      <Form<PayrollLineFormValues>
        form={form}
        layout="vertical"
        initialValues={mergedInitial}
      >
        <Row gutter={[12, 12]}>
          <Col span={12}>
            <Form.Item
              label={t("payroll.form.line_type")}
              name="lineType"
              rules={[{ required: true }]}
            >
              <CommonSelectLite
                options={lineTypeOptions}
                placeholder={t("payroll.form.line_type")}
                optionValueKey="value"
                optionLabelKey="label"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t("payroll.form.source_type")}
              name="sourceType"
              rules={[{ required: true }]}
            >
              <CommonSelectLite
                options={sourceTypeOptions}
                placeholder={t("payroll.form.source_type")}
                optionValueKey="value"
                optionLabelKey="label"
              />
            </Form.Item>
          </Col>


          <Col span={12}>
            <Form.Item
              label={t("payroll.form.amount")}
              name="amount"
              rules={[{ required: true, type: "number", min: 0 }]}
            >
              <InputNumber className="w-100" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </CustomModal>
  );
};

export default PayrollLineModal;
